import { z } from 'zod';
import { PIN_LOGIN_ROLES, type UserRole } from './roles.js';

/**
 * Subscription plans. Pricing is FLAT PER SHOP, never per seat — owners would otherwise
 * share one login to dodge it and corrupt both billing and productivity data. Each tier
 * declares its seat allowance and its module entitlements in one readable place so a
 * pricing change is a config edit, not a code hunt (task 23 enforces these).
 *
 * All prices are integer paise.
 */
export const PLAN_KEYS = ['starter', 'growth', 'pro'] as const;
export type PlanKey = (typeof PLAN_KEYS)[number];
export const planKeySchema = z.enum(PLAN_KEYS);

/**
 * Feature modules that a plan can unlock. `core` is the booking-to-delivery loop.
 *
 * `workforce` is the karigar floor — assignments, My Work, payouts, payroll, attendance and
 * capacity. It is the line between a shop the owner stitches in and a shop the owner runs.
 */
export const PLAN_MODULES = [
  'core',
  'workforce',
  'gst_invoicing',
  'expenses',
  'inventory',
  'relay_whatsapp',
  'outsourcing',
  'advanced_analytics',
] as const;
export type PlanModule = (typeof PLAN_MODULES)[number];
export const planModuleSchema = z.enum(PLAN_MODULES);

export const BILLING_CYCLES = ['monthly', 'annual'] as const;
export type BillingCycle = (typeof BILLING_CYCLES)[number];
export const billingCycleSchema = z.enum(BILLING_CYCLES);

export interface PlanDefinition {
  readonly key: PlanKey;
  /** Default English name; the UI resolves `plan.<key>.name` through i18next. */
  readonly defaultLabel: string;
  readonly monthlyPricePaise: number;
  /** Annual price — roughly two months free (ten times the monthly price). */
  readonly annualPricePaise: number;
  /**
   * Staff seats INCLUDING the owner. `null` means unlimited.
   * Seats are checked when inviting or re-activating a staff member.
   *
   * On a plan with `workforce` these are OFFICE seats: tailors, who sign in with a PIN, take
   * none. A karigar floor is what that module is sold for, and charging for every karigar
   * again as a seat would push owners back to one shared login — see {@link planCountsTailorSeats}.
   */
  readonly staffSeats: number | null;
  readonly modules: readonly PlanModule[];
  readonly sortOrder: number;
}

export const SUBSCRIPTION_PLANS: Readonly<Record<PlanKey, PlanDefinition>> = Object.freeze({
  starter: {
    key: 'starter',
    defaultLabel: 'Starter',
    monthlyPricePaise: 49_900,
    annualPricePaise: 499_000,
    staffSeats: 3,
    modules: ['core', 'gst_invoicing'],
    sortOrder: 10,
  },
  growth: {
    key: 'growth',
    defaultLabel: 'Growth',
    monthlyPricePaise: 129_900,
    annualPricePaise: 1_299_000,
    staffSeats: 5,
    modules: ['core', 'workforce', 'gst_invoicing', 'expenses', 'inventory', 'relay_whatsapp'],
    sortOrder: 20,
  },
  pro: {
    key: 'pro',
    defaultLabel: 'Pro',
    monthlyPricePaise: 249_900,
    annualPricePaise: 2_499_000,
    staffSeats: null,
    modules: [
      'core',
      'workforce',
      'gst_invoicing',
      'expenses',
      'inventory',
      'relay_whatsapp',
      'outsourcing',
      'advanced_analytics',
    ],
    sortOrder: 30,
  },
});

/** Plans in display order, cheapest first. */
export const PLANS_IN_ORDER: readonly PlanDefinition[] = Object.freeze(
  PLAN_KEYS.map((key) => SUBSCRIPTION_PLANS[key]).sort((a, b) => a.sortOrder - b.sortOrder),
);

/** Days of full access before card details are ever requested. */
export const TRIAL_DAYS = 14;

/** Days after a failed renewal during which the shop keeps full access. */
export const DUNNING_GRACE_DAYS = 7;

/** The plan a new shop is placed on for its trial. */
export const DEFAULT_TRIAL_PLAN: PlanKey = 'growth';

export function getPlan(key: PlanKey): PlanDefinition {
  return SUBSCRIPTION_PLANS[key];
}

export function planPricePaise(key: PlanKey, cycle: BillingCycle): number {
  const plan = SUBSCRIPTION_PLANS[key];
  return cycle === 'annual' ? plan.annualPricePaise : plan.monthlyPricePaise;
}

export function planHasModule(key: PlanKey, module: PlanModule): boolean {
  return SUBSCRIPTION_PLANS[key].modules.includes(module);
}

/** `null` seat allowance means unlimited, so any count fits. */
export function planAllowsStaffCount(key: PlanKey, staffCount: number): boolean {
  const seats = SUBSCRIPTION_PLANS[key].staffSeats;
  return seats === null || staffCount <= seats;
}

/**
 * True when tailors take a seat on this plan — only on a plan WITHOUT `workforce`, where a
 * tailor is just another person who signs in. Where the karigar floor is sold, it is sold
 * with as many karigars as the shop employs.
 */
export function planCountsTailorSeats(key: PlanKey): boolean {
  return !planHasModule(key, 'workforce');
}

/** True when a person with `role` takes one of this plan's seats. */
export function roleTakesSeat(key: PlanKey, role: UserRole): boolean {
  return planCountsTailorSeats(key) || !PIN_LOGIN_ROLES.includes(role);
}

/** The cheapest plan that includes `module`, or `undefined` if no plan does. */
export function cheapestPlanWithModule(module: PlanModule): PlanDefinition | undefined {
  return PLANS_IN_ORDER.find((plan) => plan.modules.includes(module));
}
